/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
    FaBoxes,
    FaWarehouse,
    FaChartBar,
    FaShippingFast,
    FaTruck,
    FaFileAlt,
    FaBuilding,
    FaTimes,
    FaBars
} from "react-icons/fa";
import { RiLogoutBoxLine } from "react-icons/ri";
import logo from "../../assets/imgs/imgs/logo__white.svg";
import logo_hfa from "../../assets/imgs/imgs/logo_white_hfa.svg";
import UserComponent from "../userComponent/userComponent";
import { logoutSelectedLink, setSelectedLink } from "../../Store/modules/selectedLinkSlice";
import { logoutSelectedCompany, setSelectedCompany } from "../../Store/modules/selectedCompany";
import { logoutSelectedMoreCompany } from "../../Store/modules/selectedMoreAbout";
import { setSelectedLanguage, logoutSelectedLanguage } from "../../Store/modules/selectedLanguage";
import { logout } from "../../Store/modules/userSlice";
import { saveLoggedInUser, removeLoggedInUser } from "../../services/Auth";
import "./navbar.scss";

function NavBar(props) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const user = useSelector((state) => state.user.data);
    const selectedLink = useSelector((state) => state.selectedLink.data);
    const company = useSelector((state) => state.selectedCompany.data);
    const selectedLanguage = useSelector((state) => state.selectedLanguage.data);

    const [selectedCompanyToSecond, setSelectedCompanyToSecond] = useState("");
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const { t, i18n } = useTranslation();

    const currentLang = selectedLanguage?.lang || i18n.language || "pt";

    // Navigation links with dedicated icons
    const navLinks = [
        { label: t('Encomendas'), linkText: 'Encomendas', state: 'open', icon: FaBoxes },
        { label: t('Artigos em Stock'), linkText: 'Artigos em Stock', state: 'mp', icon: FaWarehouse },
        { label: t('Relatório de Stocks'), linkText: 'Relatório de Stocks', state: 'rstocks', icon: FaChartBar },
        { label: t('Detalhes do Envio'), linkText: 'Detalhes do Envio', state: 'rast_env', icon: FaShippingFast },
        { label: t('Manifestos de Carga'), linkText: 'Manifestos de Carga', state: 'mcarga', icon: FaTruck },
        { label: t('Documentos'), linkText: 'Documentos', state: 'documentos', icon: FaFileAlt },
    ];

    // Language switch function
    const handleSwitchLanguage = (lang) => {
        if (lang === currentLang) return;
        i18n.changeLanguage(lang);
        dispatch(setSelectedLanguage({ data: { lang } }));
        if (user) {
            saveLoggedInUser(user.name, user.id, user.companies, lang);
        }
    };

    // Link click handler
    const handleLinkClick = (linkType, nameTitle) => {
        dispatch(
            setSelectedLink({
                data: {
                    name: linkType,
                    nameToSelect: nameTitle,
                    to: `/Pages/HomePage/${linkType}`,
                },
            })
        );
        setIsMenuOpen(false);
    };

    // Company selection handler
    const handleCompanyChange = (companyId) => {
        setSelectedCompanyToSecond(companyId);
        if (companyId) {
            dispatch(
                setSelectedCompany({
                    data: {
                        id: companyId,
                        name: user.companies[companyId],
                    },
                })
            );
        } else {
            dispatch(logoutSelectedCompany());
        }
    };

    // Auto-select single company
    useEffect(() => {
        if (user && user.companies && Object.keys(user.companies).length === 1) {
            const [firstCompanyId] = Object.entries(user.companies)[0];
            const nameCompany = user.companies[firstCompanyId];
            setSelectedCompanyToSecond(firstCompanyId);
            dispatch(
                setSelectedCompany({
                    data: {
                        id: firstCompanyId,
                        name: nameCompany,
                    },
                })
            );
        }
    }, [user]);

    // Keep local select in sync with Redux company state
    useEffect(() => {
        if (company?.id) {
            setSelectedCompanyToSecond(company.id);
        } else if (!company) {
            setSelectedCompanyToSecond("");
        }
    }, [company?.id]);

    // Close menu on route change or screen resize
    useEffect(() => {
        setIsMenuOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth > 1080 && isMenuOpen) {
                setIsMenuOpen(false);
            }
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [isMenuOpen]);

    // Prevent background scrolling when mobile menu is open
    useEffect(() => {
        if (isMenuOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [isMenuOpen]);

    // Close selected links on logo click
    const closeSelectedLink = () => {
        if (user && user.companies && Object.keys(user.companies).length === 1) {
            dispatch(logoutSelectedLink());
            dispatch(logoutSelectedMoreCompany());
        } else {
            dispatch(logoutSelectedLink());
            dispatch(logoutSelectedCompany());
            dispatch(logoutSelectedMoreCompany());
            setSelectedCompanyToSecond("");
        }
        setIsMenuOpen(false);
    };

    // Full logout for mobile menu
    const handleMobileLogout = () => {
        dispatch(logout());
        dispatch(logoutSelectedLink());
        dispatch(logoutSelectedCompany());
        dispatch(logoutSelectedMoreCompany());
        dispatch(logoutSelectedLanguage());
        removeLoggedInUser();
        setIsMenuOpen(false);
        navigate("/Pages/LoginPage/");
    };

    if (!user) return null;

    const companiesList = Object.entries(user.companies || {}).sort(([, a], [, b]) =>
        a.localeCompare(b)
    );

    return (
        <>
            <header className="app-navbar">
                <div className="navbar-container">
                    {/* Brand / Logo Section */}
                    <div className="navbar-brand-section">
                        <Link className="navbar-brand-link" onClick={closeSelectedLink} to="/pages/HomePage/">
                            <img className="logo-hfa" src={logo_hfa} alt="HFA Logo" />
                            <img className="logo-text" src={logo} alt="Extranet" />
                        </Link>
                    </div>

                    {/* Desktop Navigation Links */}
                    <nav className="navbar-desktop-nav">
                        {navLinks.map((link) => {
                            const targetPath = `/Pages/HomePage/${link.linkText}`;
                            const isActive = selectedLink?.to === targetPath;
                            const IconComponent = link.icon;

                            return (
                                <NavLink
                                    key={link.linkText}
                                    className={`nav-item-link ${isActive ? 'active' : ''}`}
                                    onClick={() => handleLinkClick(link.linkText, link.state)}
                                    to={targetPath}
                                >
                                    <IconComponent className="nav-item-icon" />
                                    <span>{link.label}</span>
                                </NavLink>
                            );
                        })}
                    </nav>

                    {/* Desktop Actions (Company + Language + User) */}
                    <div className="navbar-desktop-actions">
                        {/* Company Selector */}
                        <div className="navbar-company-selector">
                            <FaBuilding className="company-icon" />
                            <select
                                value={selectedCompanyToSecond || ""}
                                className="company-select-input"
                                onChange={(e) => handleCompanyChange(e.target.value)}
                                title={t('Selecionar Cliente')}
                            >
                                {companiesList.length === 1 ? (
                                    companiesList.map(([id, name]) => (
                                        <option key={id} value={id}>
                                            {name}
                                        </option>
                                    ))
                                ) : (
                                    <>
                                        <option value="">{t('Selecionar Cliente')}</option>
                                        {companiesList.map(([id, name]) => (
                                            <option key={id} value={id}>
                                                {name}
                                            </option>
                                        ))}
                                    </>
                                )}
                            </select>
                        </div>

                        {/* Language Pill Switcher */}
                        <div className="navbar-lang-pill">
                            <button
                                type="button"
                                className={`lang-btn ${currentLang === 'pt' ? 'active' : ''}`}
                                onClick={() => handleSwitchLanguage('pt')}
                                title="Português"
                            >
                                PT
                            </button>
                            <button
                                type="button"
                                className={`lang-btn ${currentLang === 'en' ? 'active' : ''}`}
                                onClick={() => handleSwitchLanguage('en')}
                                title="English"
                            >
                                EN
                            </button>
                        </div>

                        {/* User Profile */}
                        <div className="navbar-user-wrapper">
                            <UserComponent
                                callback={() => {
                                    handleSwitchLanguage("pt");
                                    setSelectedCompanyToSecond("");
                                }}
                            />
                        </div>
                    </div>

                    {/* Mobile Top Controls (Language + User + Hamburger) */}
                    <div className="navbar-mobile-controls">
                        <div className="navbar-lang-pill mobile-lang">
                            <button
                                type="button"
                                className={`lang-btn ${currentLang === 'pt' ? 'active' : ''}`}
                                onClick={() => handleSwitchLanguage('pt')}
                            >
                                PT
                            </button>
                            <button
                                type="button"
                                className={`lang-btn ${currentLang === 'en' ? 'active' : ''}`}
                                onClick={() => handleSwitchLanguage('en')}
                            >
                                EN
                            </button>
                        </div>

                        <div className="mobile-user">
                            <UserComponent
                                callback={() => {
                                    handleSwitchLanguage("pt");
                                    setSelectedCompanyToSecond("");
                                }}
                            />
                        </div>

                        <button
                            type="button"
                            className="navbar-hamburger-btn"
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            aria-label="Toggle navigation"
                        >
                            {isMenuOpen ? <FaTimes size={20} /> : <FaBars size={20} />}
                        </button>
                    </div>
                </div>

                {/* Mobile Backdrop Overlay */}
                {isMenuOpen && (
                    <div
                        className="navbar-mobile-backdrop"
                        onClick={() => setIsMenuOpen(false)}
                    />
                )}

                {/* Mobile Drawer Menu */}
                <div className={`navbar-mobile-drawer ${isMenuOpen ? 'open' : ''}`}>
                    <div className="mobile-drawer-content">
                        {/* Company Selector in Mobile Drawer */}
                        <div className="mobile-drawer-section">
                            <label className="mobile-section-label">
                                <FaBuilding className="mr-1" /> {t('Empresa / Cliente')}
                            </label>
                            <div className="mobile-company-select-box">
                                <select
                                    value={selectedCompanyToSecond || ""}
                                    className="mobile-company-select"
                                    onChange={(e) => {
                                        handleCompanyChange(e.target.value);
                                        setIsMenuOpen(false);
                                    }}
                                >
                                    {companiesList.length === 1 ? (
                                        companiesList.map(([id, name]) => (
                                            <option key={id} value={id}>
                                                {name}
                                            </option>
                                        ))
                                    ) : (
                                        <>
                                            <option value="">{t('Selecionar Cliente')}</option>
                                            {companiesList.map(([id, name]) => (
                                                <option key={id} value={id}>
                                                    {name}
                                                </option>
                                            ))}
                                        </>
                                    )}
                                </select>
                            </div>
                        </div>

                        {/* Navigation Links in Mobile Drawer */}
                        <div className="mobile-drawer-section">
                            <label className="mobile-section-label">{t('Navegação')}</label>
                            <nav className="mobile-nav-links-list">
                                {navLinks.map((link) => {
                                    const targetPath = `/Pages/HomePage/${link.linkText}`;
                                    const isActive = selectedLink?.to === targetPath;
                                    const IconComponent = link.icon;

                                    return (
                                        <NavLink
                                            key={link.linkText}
                                            className={`mobile-nav-item ${isActive ? 'active' : ''}`}
                                            onClick={() => handleLinkClick(link.linkText, link.state)}
                                            to={targetPath}
                                        >
                                            <IconComponent className="mobile-nav-icon" />
                                            <span>{link.label}</span>
                                        </NavLink>
                                    );
                                })}
                            </nav>
                        </div>

                        {/* Logout in Mobile Drawer */}
                        <div className="mobile-drawer-footer">
                            <button
                                type="button"
                                className="mobile-logout-btn"
                                onClick={handleMobileLogout}
                            >
                                <RiLogoutBoxLine size={18} className="mr-2" />
                                <span>{t('Sair')}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </header>
        </>
    );
}

export default NavBar;
